/* @jsxImportSource react */

import { type ReactNode, Suspense, type FC } from 'react';
import { SWRConfig } from 'swr';

import config from '@packages/config';
import type { Locale } from '@packages/config';
import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import type { Device } from '@packages/shared/src/utils/types';

import { AppBanner } from '../AppBanner';
import { DynamicInspiringSearchOnboardingModal } from '../../InspiringSearch/InspiringSearchOnboardingModal/InspiringSearchOnboardingModal.dynamic';
import { ResponsiveNavigationLoaderServer } from '../Navigation/ResponsiveNavigation/ResponsiveNavigationLoader.Server';
import { ResponsiveHeaderLayout } from '../ResponsiveHeaderLayout';
import { LinkedLogo } from '../Toolbar/LinkedLogo';
import { DynamicSearchField } from '../Toolbar/SearchField/SearchField.dynamic';
import { SlotsDefault } from '../Toolbar/Slots/SlotsDefault';
import { ResponsiveNavigationLoading } from '../Navigation/ResponsiveNavigation/ResponsiveNavigation.loading';
import { SlotsDefaultLoading } from '../Toolbar/Slots/SlotsDefault.loading';
import { BypassHeaderAndSearchLinks } from '../ResponsiveHeaderLayout.BypassBlocks';

type DefaultHeaderProps = {
  locale: Locale;
  device: Device;
  /** Only for compatibility with apps/header, to make it possible to use the navigation in the pages router still */
  navigation?: ReactNode;
};

/**
 * Integrated header module with appbanner (only mobile), toolbar and navigation.
 * This component is able to get its data whether from client or it accepts the SEO relevant initial data from server to populate SWR cache and to make its further fetches and revalidations on client.
 */
export const DefaultHeader: FC<DefaultHeaderProps> = ({ locale, device, navigation }) => {
  const {
    appBanner: { enabled: appBannerEnabled },
  } = config;

  return (
    <>
      <BypassHeaderAndSearchLinks />
      <ErrorBoundary>{device === 'mobile' && appBannerEnabled && <AppBanner />}</ErrorBoundary>
      <ResponsiveHeaderLayout
        logo={<LinkedLogo />}
        searchField={
          <SWRConfig>
            <DynamicSearchField />
          </SWRConfig>
        }
        icons={
          <Suspense fallback={<SlotsDefaultLoading />}>
            <SlotsDefault />
          </Suspense>
        }
        navigation={
          <Suspense fallback={<ResponsiveNavigationLoading />}>
            {navigation ?? <ResponsiveNavigationLoaderServer locale={locale} device={device} />}
          </Suspense>
        }
      />

      <ErrorBoundary>
        <DynamicInspiringSearchOnboardingModal />
      </ErrorBoundary>
    </>
  );
};
